import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaCheckCircle, FaUserMd, FaTrophy,
    FaStar, FaUsers, FaHospital, FaClock,
    FaWhatsapp, FaPhone, FaAward, FaShieldAlt,
    FaTooth, FaMicroscope, FaHeartbeat, FaSmile,
    FaCertificate, FaHandHoldingHeart, FaStethoscope,
    FaLaptopMedical, FaCalendarCheck
} from 'react-icons/fa';
import './HeroSection.css'; // Leaving this for now, but most styles will be utility-based

const HeroSection = () => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [showFloatingCTA, setShowFloatingCTA] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const offset = window.scrollY;
            setScrolled(offset > 400); // Show mini header after scrolling down
            setShowFloatingCTA(offset > 500); // Show floating CTA
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleButtonClick = () => {
        if (navigator.vibrate) navigator.vibrate(15);
        navigate('/contact');
    };

    const handleWhatsappClick = () => {
        if (navigator.vibrate) navigator.vibrate(15);
        window.open('https://wa.me/918076353837', '_blank');
    };

    const handlePhoneClick = () => {
        if (navigator.vibrate) navigator.vibrate(15);
        window.location.href = `tel: +918076353837`;
    };

    const benefits = [
        { icon: <FaCheckCircle />, text: 'Painless Treatments' },
        { icon: <FaUsers />, text: 'Experienced Specialists' },
        { icon: <FaTrophy />, text: 'Best Clinic in Gurgaon' },
        { icon: <FaHospital />, text: 'Modern Facilities' },
        { icon: <FaClock />, text: '24/7 Emergency Care' },
    ];

    const stats = [
        { icon: <FaStar />, text: '5-star Google Reviews', sub: '(55+ Reviews)' },
        { icon: <FaTrophy />, text: 'NABH Accredited', sub: 'Clinic' },
        { icon: <FaUserMd />, text: 'Certified & Experienced', sub: 'Dentists' },
        { icon: <FaUsers />, text: '1000+ Happy Patients', sub: 'Trusted Care' },
        { icon: <FaAward />, text: '5+ Years Experience', sub: 'Expert Team' },
        { icon: <FaShieldAlt />, text: 'Pain-Free Treatments', sub: 'Comfort First' },
        { icon: <FaLaptopMedical />, text: 'Advanced Technology', sub: 'Modern Equipment' },
        { icon: <FaClock />, text: 'Same-Day Appointments', sub: 'Quick Service' },
        { icon: <FaCertificate />, text: 'ISO Certified', sub: 'Quality Assured' },
        // { icon: <FaHandHoldingHeart />, text: 'Insurance Accepted', sub: 'Easy Payments' },
        { icon: <FaSmile />, text: 'Smile Makeovers', sub: 'Transform Lives' },
        { icon: <FaTooth />, text: 'All Dental Services', sub: 'Under One Roof' },
    ];

    return (
        <div className="relative bg-gradient-to-br from-blue-50 to-white overflow-hidden pt-24 md:pt-28">
            {/* Sticky Mini Header */}
            <AnimatePresence>
                {scrolled && (
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        className="fixed bottom-0 left-0 right-0 h-16 bg-white/90 backdrop-blur-md shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50 flex items-center justify-between px-6 border-t border-gray-100"
                    >
                        <span className="font-bold text-lg text-primary-900">Wisdom Dental</span>
                        <button
                            onClick={handleButtonClick}
                            className="bg-primary-600 text-white px-5 py-2 rounded-full font-semibold shadow-lg hover:bg-primary-700 transition"
                        >
                            Book Now
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-20 flex flex-col-reverse lg:flex-row items-center gap-8 lg:gap-12">

                {/* Left Content */}
                <div className="flex-1 w-full text-center lg:text-left">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-2xl lg:text-3xl font-bold leading-tight text-gray-900 mb-4 lg:mb-6">
                            <span className="eyebrow text-sm lg:text-base text-gray-600 font-semibold uppercase tracking-wide block mb-2">PREMIUM DENTAL CLINIC IN SECTOR 23, GURGAON</span>
                            <br />
                            Pain-Free Smiles Start Here!
                        </h1>
                        <p className="text-base lg:text-lg text-gray-600 mb-6 lg:mb-8 max-w-xl mx-auto lg:mx-0">
                            Expert dental care by Dr. Muskan in Sector 23, Gurgaon. Trusted by 1000+ patients for advanced, pain-free treatments and a 5-star experience.
                        </p>

                        <div className="hidden md:flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8 lg:mb-12">
                            <button
                                onClick={handleButtonClick}
                                className="w-full sm:w-auto bg-primary-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl shadow-primary-600/30 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 relative overflow-hidden group"
                            >
                                <span className="relative z-10 flex items-center justify-center gap-2">
                                    Book Appointment Now
                                </span>
                                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                            </button>
                            <span className="text-sm text-gray-500 font-medium">
                                <span className="text-green-500 mr-1">●</span>
                                Available Today
                            </span>
                        </div>

                        {/* Benefits Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
                            {benefits.map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="p-4 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center hover:shadow-md transition-shadow"
                                >
                                    <div className="text-primary-500 text-2xl mb-2">{item.icon}</div>
                                    <span className="text-sm font-medium text-gray-700">{item.text}</span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Right Image (Mobile Order 1) */}
                <div className="flex-1 w-full flex justify-center lg:justify-end relative">
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="relative z-10 w-full max-w-[400px]"
                    >
                        <div className="absolute inset-0 bg-gradient-to-tr from-blue-100 to-pink-100 rounded-full blur-3xl opacity-50 -z-10 transform scale-110" />
                        <img
                            src="DrMuskan_Clinic_Final_v2.jpg"
                            alt="Dr. Muskan - Wisdom Dental Clinic"
                            className="w-full h-auto object-cover rounded-3xl shadow-2xl"
                            loading="eager"
                        />

                        {/* Floating Badge */}
                        <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                            className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3"
                        >
                            <div className="bg-yellow-100 p-2 rounded-full text-yellow-600">
                                <FaStar />
                            </div>
                            <div>
                                <div className="font-bold text-gray-900">4.9/5</div>
                                <div className="text-xs text-gray-500">Google Rating</div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* Social Proof Strip */}
            <div className="bg-white border-y border-gray-100 py-8 overflow-hidden">
                <div className="relative">
                    <div className="scrolling-stats-container">
                        <div className="scrolling-stats-content">
                            {/* First set */}
                            {stats.map((stat, idx) => (
                                <div key={`first-${idx}`} className="flex items-center gap-4 group flex-shrink-0 px-8">
                                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-primary-600 group-hover:scale-110 transition-transform">
                                        {stat.icon}
                                    </div>
                                    <div>
                                        <div className="font-bold text-gray-900 whitespace-nowrap">{stat.text}</div>
                                        <div className="text-sm text-gray-500 whitespace-nowrap">{stat.sub}</div>
                                    </div>
                                </div>
                            ))}
                            {/* Duplicate set for seamless loop */}
                            {stats.map((stat, idx) => (
                                <div key={`second-${idx}`} className="flex items-center gap-4 group flex-shrink-0 px-8">
                                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-primary-600 group-hover:scale-110 transition-transform">
                                        {stat.icon}
                                    </div>
                                    <div>
                                        <div className="font-bold text-gray-900 whitespace-nowrap">{stat.text}</div>
                                        <div className="text-sm text-gray-500 whitespace-nowrap">{stat.sub}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating CTAs */}
            <div className={`hidden md:flex fixed right-6 bottom-24 flex-col gap-4 z-40 transition-all duration-300 ${showFloatingCTA ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}>
                <button
                    onClick={handlePhoneClick}
                    className="w-12 h-12 bg-blue-500 text-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition active:scale-95"
                    aria-label="Call Now"
                >
                    <FaPhone />
                </button>
                <button
                    onClick={handleWhatsappClick}
                    className="w-12 h-12 bg-green-500 text-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition active:scale-95"
                    aria-label="WhatsApp"
                >
                    <FaWhatsapp className="text-xl" />
                </button>
            </div>
        </div>
    );
};

export default HeroSection;
